import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useCart } from '../Context/CartContext'
import { useCurrency } from '../Context/CurrencyContext'
import MainHeader from './MainHeader'
import HomeFooter from './HomeFooter'
import useTemplateScripts from '../hooks/useTemplateScripts'
import { SHOP_CATEGORIES, classifyCategory, collectionPath } from '../constants/shopCategories'
import { CONTACT_INFO, SITE_LINKS } from '../constants/siteLinks'
import { useConfirm } from './AdminUI'
import './Cart.css'

const CHECKOUT_STEPS = [
  { id: 'cart', label: 'Cart', icon: 'fa-cart-shopping' },
  { id: 'details', label: 'Details & payment', icon: 'fa-credit-card' },
  { id: 'done', label: 'Confirmation', icon: 'fa-circle-check' },
]

const PAYMENT_LOGOS = [
  { name: 'MTN Mobile Money', src: '/assets/img/brands/MTN-logo.jpg' },
  { name: 'Airtel Money', src: '/assets/img/brands/Airtel-logo.png' },
]

const CART_PERKS = [
  { icon: 'fa-shield-halved', title: 'Secure checkout', text: 'Order confirmation by email' },
  { icon: 'fa-truck-fast', title: 'Free delivery', text: 'We confirm scheduling after payment' },
  { icon: 'fa-certificate', title: 'Genuine products', text: 'With warranty cover' },
]

const productPath = (item) => `/product-details/${item.productId || item.id}`

/** Sub-category name for a cart item (e.g. "CCTV Cameras"), when it has a category. */
const typeOf = (item, name) => {
  const raw = Array.isArray(item.category) ? item.category.filter(Boolean)[0] : item.category
  return raw ? classifyCategory(raw, name).subcategoryName : ''
}

const Cart = () => {
  useTemplateScripts()
  const navigate = useNavigate()

  const { cartItems, removeFromCart, updateQuantity, clearCart, getTotalPrice } = useCart()
  const { formatPrice, isForeign, currency, baseCurrency, setCurrency, currencies } = useCurrency()
  const [toast, setToast] = useState(null)
  const [confirm, confirmDialog] = useConfirm()

  useEffect(() => {
    if (!toast) return undefined
    const timer = setTimeout(() => setToast(null), 3000)
    return () => clearTimeout(timer)
  }, [toast])

  const subtotal = getTotalPrice()
  const shipping = 0
  const total = subtotal + shipping
  const itemCount = cartItems.reduce((sum, item) => sum + (item.quantity || 0), 0)

  const handleQuantityChange = (itemId, newQuantity) => {
    if (newQuantity < 1) return
    updateQuantity(itemId, newQuantity)
  }

  const handleRemove = (itemId, itemName) => {
    removeFromCart(itemId)
    setToast(`${itemName} removed from cart`)
  }

  const handleClearCart = () => {
    confirm({
      icon: 'fa-cart-arrow-down',
      title: 'Clear your cart?',
      message: 'All items will be removed from your cart. You can add them again from the shop.',
      item: {
        media: (
          <span className="ax-avatar ax-avatar--lg ax-avatar--soft">
            <i className="fas fa-cart-shopping" />
          </span>
        ),
        title: `${itemCount} item${itemCount === 1 ? '' : 's'} in your cart`,
        meta: `Subtotal ${formatPrice(subtotal)}`,
      },
      confirmLabel: 'Clear cart',
      cancelLabel: 'Keep items',
      confirmIcon: 'fa-trash-can',
      onConfirm: () => {
        clearCart()
        setToast('Cart cleared')
      },
    })
  }

  const empty = cartItems.length === 0

  return (
    <div className="ct-page">
      <MainHeader cartCount={itemCount} />

      <main className="ct-main">
        <div className="ct-container">
          <section className="ct-hero">
            <div className="ct-hero__copy">
              <nav className="ct-crumbs" aria-label="Breadcrumb">
                <Link to={SITE_LINKS.home}>Home</Link>
                <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                <Link to={SITE_LINKS.shop}>Shop</Link>
                <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                <span aria-current="page">Cart</span>
              </nav>
              <h1 className="ct-hero__title">Shopping Cart</h1>
              <p className="ct-hero__text">
                {empty
                  ? 'Your cart is empty — browse the shop to add products.'
                  : `${itemCount} item${itemCount === 1 ? '' : 's'} ready for checkout. Review quantities, then continue to payment.`}
              </p>
            </div>
            <ol className="ct-steps" aria-label="Checkout progress">
              {CHECKOUT_STEPS.map((step, i) => (
                <li key={step.id} className={i === 0 ? 'is-current' : ''} aria-current={i === 0 ? 'step' : undefined}>
                  <span className="ct-steps__dot" aria-hidden="true">
                    <i className={`fa-solid ${step.icon}`} />
                  </span>
                  <span className="ct-steps__label">
                    <small>Step {i + 1}</small>
                    {step.label}
                  </span>
                </li>
              ))}
            </ol>
          </section>

          {empty ? (
            <section className="ct-empty">
              <span className="ct-empty__art" aria-hidden="true">
                <i className="fa-solid fa-cart-shopping" />
              </span>
              <h2 className="ct-empty__title">Your cart is empty</h2>
              <p className="ct-empty__text">Add products from the shop, then come back here to review and complete your order.</p>
              <div className="ct-empty__actions">
                <Link to={SITE_LINKS.shop} className="ct-btn ct-btn--primary">
                  Browse the shop
                  <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                </Link>
                <Link to={SITE_LINKS.wishlist} className="ct-btn ct-btn--outline">
                  <i className="fa-solid fa-heart" aria-hidden="true" />
                  View wishlist
                </Link>
              </div>
              <div className="ct-empty__cats">
                <span>Shop by category</span>
                <ul>
                  {SHOP_CATEGORIES.map((cat) => (
                    <li key={cat.id}>
                      <Link to={collectionPath(cat.id)} className="ct-catlink">
                        <i className={`fa-solid ${cat.icon}`} aria-hidden="true" />
                        {cat.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          ) : (
            <div className="ct-layout">
              <section className="ct-panel" aria-labelledby="ct-items-heading">
                <header className="ct-panel__head">
                  <h2 id="ct-items-heading">
                    Your items <span>({itemCount})</span>
                  </h2>
                  <button type="button" className="ct-clear" onClick={handleClearCart}>
                    <i className="fa-solid fa-trash-can" aria-hidden="true" />
                    Clear cart
                  </button>
                </header>

                <div className="ct-cols" aria-hidden="true">
                  <span>Product</span>
                  <span>Price</span>
                  <span>Quantity</span>
                  <span>Total</span>
                </div>

                <ul className="ct-lines">
                  {cartItems.map((item, index) => {
                    const name = item.name || item.title || 'Product'
                    const unit = item.price || 0
                    const quantity = item.quantity || 0
                    const type = typeOf(item, name)
                    return (
                      <li key={item.id || index} className="ct-line">
                        <div className="ct-line__product">
                          <Link to={productPath(item)} className="ct-line__thumb">
                            <img
                              src={item.image || item.img || '/placeholder-image.png'}
                              alt=""
                              onError={(e) => {
                                e.target.onerror = null
                                e.target.src = '/placeholder-image.png'
                              }}
                            />
                          </Link>
                          <div className="ct-line__info">
                            {type && <span className="ct-line__type">{type}</span>}
                            <Link to={productPath(item)} className="ct-line__name">
                              {name}
                            </Link>
                            <span className="ct-line__stock">
                              <i className="fa-solid fa-circle-check" aria-hidden="true" />
                              Free delivery
                            </span>
                          </div>
                        </div>

                        <div className="ct-line__price">
                          <small>Price</small>
                          {formatPrice(unit)}
                        </div>

                        <div className="ct-qty" role="group" aria-label={`Quantity for ${name}`}>
                          <button
                            type="button"
                            disabled={quantity <= 1}
                            onClick={() => handleQuantityChange(item.id, quantity - 1)}
                            aria-label="Decrease quantity"
                          >
                            <i className="fa-solid fa-minus" aria-hidden="true" />
                          </button>
                          <span aria-live="polite">{quantity}</span>
                          <button type="button" onClick={() => handleQuantityChange(item.id, quantity + 1)} aria-label="Increase quantity">
                            <i className="fa-solid fa-plus" aria-hidden="true" />
                          </button>
                        </div>

                        <div className="ct-line__total">
                          <small>Total</small>
                          {formatPrice(unit * quantity)}
                        </div>

                        <button
                          type="button"
                          className="ct-line__remove"
                          onClick={() => handleRemove(item.id, name)}
                          title="Remove"
                          aria-label={`Remove ${name}`}
                        >
                          <i className="fa-solid fa-xmark" aria-hidden="true" />
                        </button>
                      </li>
                    )
                  })}
                </ul>

                <footer className="ct-panel__foot">
                  <Link to={SITE_LINKS.shop} className="ct-back">
                    <i className="fa-solid fa-arrow-left" aria-hidden="true" />
                    Continue shopping
                  </Link>
                  <a href={CONTACT_INFO.phoneHref} className="ct-help">
                    <i className="fa-solid fa-headset" aria-hidden="true" />
                    Need help? <strong>{CONTACT_INFO.phone}</strong>
                  </a>
                </footer>
              </section>

              <aside className="ct-summary" aria-labelledby="ct-summary-heading">
                <div className="ct-summary__card">
                  <div className="ct-summary__head">
                    <h2 id="ct-summary-heading">Order summary</h2>
                    <label className="ct-currency" title="Display currency">
                      <span className="visually-hidden">Display currency</span>
                      <i className="fa-solid fa-coins" aria-hidden="true" />
                      <select value={currency} onChange={(e) => setCurrency(e.target.value)} aria-label="Display currency">
                        {currencies.map((c) => (
                          <option key={c.code} value={c.code}>
                            {c.code}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  {isForeign ? (
                    <p className="ct-note" role="note">
                      <i className="fa-solid fa-circle-info" aria-hidden="true" />
                      <span>
                        Showing prices in <strong>{currency}</strong>. Payment at checkout is in <strong>{baseCurrency}</strong>.
                      </span>
                    </p>
                  ) : null}

                  <dl className="ct-rows">
                    <div>
                      <dt>
                        Subtotal ({itemCount} item{itemCount === 1 ? '' : 's'})
                      </dt>
                      <dd>{formatPrice(subtotal)}</dd>
                    </div>
                    <div>
                      <dt>Delivery</dt>
                      <dd className="is-free">Free</dd>
                    </div>
                  </dl>

                  <div className="ct-total">
                    <span>Total</span>
                    <strong>{formatPrice(total)}</strong>
                  </div>

                  <button type="button" className="ct-btn ct-btn--primary ct-btn--block ct-btn--lg" onClick={() => navigate('/checkout')}>
                    <i className="fa-solid fa-lock" aria-hidden="true" />
                    Proceed to checkout
                  </button>

                  <div className="ct-pay">
                    <span>We accept</span>
                    <ul>
                      {PAYMENT_LOGOS.map((logo) => (
                        <li key={logo.name}>
                          <img src={logo.src} alt={logo.name} title={logo.name} />
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <ul className="ct-perks">
                  {CART_PERKS.map((perk) => (
                    <li key={perk.title}>
                      <span className="ct-perks__icon" aria-hidden="true">
                        <i className={`fa-solid ${perk.icon}`} />
                      </span>
                      <span>
                        <strong>{perk.title}</strong>
                        {perk.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </aside>
            </div>
          )}
        </div>
      </main>

      <HomeFooter />
      {confirmDialog}

      {!empty && (
        <div className="ct-mobilebar">
          <span>
            <small>Total</small>
            <strong>{formatPrice(total)}</strong>
          </span>
          <button type="button" className="ct-btn ct-btn--primary" onClick={() => navigate('/checkout')}>
            Checkout
            <i className="fa-solid fa-arrow-right" aria-hidden="true" />
          </button>
        </div>
      )}

      {toast && (
        <div className="ct-toast" role="status">
          <i className="fa-solid fa-circle-check" aria-hidden="true" />
          {toast}
        </div>
      )}
    </div>
  )
}

export default Cart
